import React, { createContext, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useAccountContext } from '@account/contexts/core'
import { useMeContext } from '@contexts/MeContext'
import { useSiteContext } from '@contexts/MkSiteContext'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { brandDisplayName } from '@typescript'
import { getPartyIdFromMakoJwt } from '@utils/authJwtBrand'
import { normalizePathnameForAnalytics } from '../pathUtils'
import { dispatchGtmEvent, primeDataLayerKeyOrder } from '../providers/gtmAdapter'
import type { AnalyticsEvent, AnalyticsGlobalContext, CustomPageViewEvent, DynamicClickTrackingEvent, LoginEvent } from '../types'
import { clearPendingLoginAnalytics, consumePendingLoginAnalytics } from '../pendingLoginAnalytics'
import { mergePointsAndOwnershipSdr } from './sdrAccountMerge'
import { sdrFieldsFromMe } from './sdrMeFields'

export type AnalyticsProviderExtra = Readonly<Record<string, string | number | boolean | undefined>>

/**
 * Snapshot consumed by `useTrackEvent`. `AnalyticsBootstrapProvider` / `AnalyticsProvider` own
 * the sources of truth (SDR fields, `extraContext`) and republish them through this shape.
 */
export type AnalyticsState = {
  readonly globalContext: AnalyticsGlobalContext
  readonly extras: Readonly<Record<string, string>>
}

/**
 * Live `AnalyticsState`. Default is an empty state so `useTrackEvent` still dispatches (just
 * without globals) if a consumer somehow ends up outside both providers; in normal app flow
 * `AnalyticsBootstrapProvider` is mounted high in `AppProviders` and shadows this default.
 */
export const AnalyticsStateContext = createContext<AnalyticsState>({ globalContext: {}, extras: {} })

// Inlined by the bundler at build time.
const RELEASE_VERSION = process.env.npm_package_version

/** Pre-auth globals shared by `AnalyticsBootstrapProvider` and `AnalyticsProvider`. */
function buildPreAuthGlobalContext(themeBrand: string, site: string | undefined): AnalyticsGlobalContext {
  const merged: AnalyticsGlobalContext = {
    brand: site ?? brandDisplayName(themeBrand),
    platform: 'Web',
  }
  if (RELEASE_VERSION) {
    merged.release_version = RELEASE_VERSION
  }
  return merged
}

/** Skip click tracking for this element or any ancestor (e.g. cookie UI, devtools). */
const ANALYTICS_IGNORE_SELECTOR = '[data-analytics-ignore]'

/** Visible label first (`innerText` / input `value`), then `aria-label` if no visible text. */
function resolveButtonClickEventLabel(element: HTMLElement): string | undefined {
  if (element.closest(ANALYTICS_IGNORE_SELECTOR)) {
    return undefined
  }

  if (element instanceof HTMLInputElement) {
    const inputType = element.type
    if (inputType !== 'button' && inputType !== 'submit') {
      return undefined
    }
    const value = element.value?.trim()
    if (value) {
      return value
    }
    const aria = element.getAttribute('aria-label')?.trim()
    return aria || undefined
  }

  const visible = element.innerText?.replace(/\s+/g, ' ').trim() || element.textContent?.replace(/\s+/g, ' ').trim()
  if (visible) {
    return visible.length > 300 ? `${visible.slice(0, 297)}...` : visible
  }

  const ariaOnly = element.getAttribute('aria-label')?.trim()
  return ariaOnly ? (ariaOnly.length > 300 ? `${ariaOnly.slice(0, 297)}...` : ariaOnly) : undefined
}

/** Nearest interactive button-like control from event target (capture-safe). */
function findClickableButtonTarget(from: Element): HTMLElement | null {
  const el = from.closest('button, input[type="button"], input[type="submit"], [role="button"]')
  return el instanceof HTMLElement ? el : null
}

function hasLoggedInAnalyticsGlobals(globalContext: AnalyticsGlobalContext): boolean {
  return typeof globalContext.membership_number === 'string' && globalContext.membership_number.length > 0
}

export type AnalyticsProviderProps = {
  children: React.ReactNode
  /**
   * Optional: small non-PII extras (UTM, campaign, experiment) merged into every dataLayer push
   * at the lowest priority. Stays separate from `AnalyticsGlobalContext` because well-known SDR
   * keys are strictly typed in `types.ts`; extras are an explicit free-form pass-through.
   * Memoize if object identity changes.
   */
  extraContext?: AnalyticsProviderExtra
}

const STABLE_EMPTY_EXTRA: AnalyticsProviderExtra = {}
/** Dedup CUSTOM_PAGE_VIEW across auth transitions that remount account providers. */
let lastFiredCustomPageViewKey: string | undefined

/**
 * Builds the global variables layer + extras, exposes them via `AnalyticsStateContext` for
 * `useTrackEvent`, fires `custom_page_view` on SPA navigation, and runs a capture-phase
 * `document` click listener that emits `dynamic_click_tracking` for primary clicks on
 * button-like controls. Must be inside `MkAccountProvider`.
 * @see ./README.md
 */
export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children, extraContext = STABLE_EMPTY_EXTRA }) => {
  const { themeBrand, site } = useSiteContext()
  const { me, isLoading: meLoading } = useMeContext()
  const { isAuthorized, session } = useAuthentication()
  const { pathname, key: locationKey } = useLocation()
  const {
    pointsBenefitsData,
    pointsBenefitsLoading,
    ownershipData,
    ownershipLoading,
  } = useAccountContext()
  const wasAuthorizedRef = React.useRef(isAuthorized)

  const accountSdr = useMemo(() => mergePointsAndOwnershipSdr(pointsBenefitsData, ownershipData), [pointsBenefitsData, ownershipData])

  const makoJwtToken = useMemo(() => {
    if (!isAuthorized || !session || session.sessionType !== 'authorized') {
      return undefined
    }
    return session.jwtToken
  }, [isAuthorized, session])

  const globalContext = useMemo((): AnalyticsGlobalContext => {
    const merged: AnalyticsGlobalContext = {
      ...buildPreAuthGlobalContext(themeBrand, site),
      ...sdrFieldsFromMe(me),
      ...accountSdr,
    }

    const partyId = getPartyIdFromMakoJwt(makoJwtToken)
    if (partyId) {
      merged.party_id = partyId
    }

    return merged
  }, [themeBrand, site, me, accountSdr, makoJwtToken])

  const extras = useMemo((): Readonly<Record<string, string>> => {
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(extraContext)) {
      if (v !== undefined && v !== null && v !== '') {
        out[k] = String(v)
      }
    }
    return out
  }, [extraContext])

  const state = useMemo<AnalyticsState>(() => ({ globalContext, extras }), [globalContext, extras])
  const isLoggedInAnalyticsReady =
    isAuthorized &&
    session?.sessionType === 'authorized' &&
    hasLoggedInAnalyticsGlobals(globalContext) &&
    !meLoading &&
    !pointsBenefitsLoading &&
    !ownershipLoading

  const trackEvent = React.useCallback(
    <E extends AnalyticsEvent>(event: E) => {
      type DeclaredGlobalKeys = Extract<keyof E['payload'], keyof AnalyticsGlobalContext>
      type DeclaredGlobals = Pick<E['payload'], DeclaredGlobalKeys>
      const declaredGlobals = state.globalContext as DeclaredGlobals

      dispatchGtmEvent<E>({
        ...event,
        payload: { ...state.extras, ...declaredGlobals, ...event.payload },
      } as E)
    },
    [state],
  )

  // Must commit before CUSTOM_PAGE_VIEW so the primer registers keys first.
  useEffect(() => {
    primeDataLayerKeyOrder()
  }, [])

  useEffect(() => {
    const onClickCapture = (ev: MouseEvent) => {
      if (ev.button !== 0) {
        return
      }
      const t = ev.target
      if (!(t instanceof Element)) {
        return
      }
      const btn = findClickableButtonTarget(t)
      if (!btn || btn.closest(ANALYTICS_IGNORE_SELECTOR)) {
        return
      }
      const label = resolveButtonClickEventLabel(btn)
      if (label) {
        trackEvent<DynamicClickTrackingEvent>({ type: 'DYNAMIC_CLICK_TRACKING', payload: { event_label: label } })
      }
    }

    document.addEventListener('click', onClickCapture, true)
    return () => document.removeEventListener('click', onClickCapture, true)
  }, [trackEvent])

  useEffect(() => {
    if (lastFiredCustomPageViewKey === locationKey) return
    lastFiredCustomPageViewKey = locationKey
    trackEvent<CustomPageViewEvent>({ type: 'CUSTOM_PAGE_VIEW', payload: { custom_page_name: normalizePathnameForAnalytics(pathname) } })
  }, [locationKey, pathname, trackEvent])

  useEffect(() => {
    if (!isAuthorized && wasAuthorizedRef.current) {
      clearPendingLoginAnalytics()
    }
    wasAuthorizedRef.current = isAuthorized
  }, [isAuthorized])

  useEffect(() => {
    if (!isLoggedInAnalyticsReady) {
      return
    }

    const pending = consumePendingLoginAnalytics()
    if (!pending) {
      return
    }

    trackEvent<LoginEvent>({ type: 'LOGIN', payload: { custom_page_name: pending.custom_page_name } })
  }, [isLoggedInAnalyticsReady, trackEvent])

  return <AnalyticsStateContext.Provider value={state}>{children}</AnalyticsStateContext.Provider>
}

/**
 * Pre-auth `AnalyticsStateContext` (brand/platform/release_version) for callers above
 * `SessionAwareAccountProvider` (e.g. SignIn). The full `AnalyticsProvider` shadows it below.
 */
export const AnalyticsBootstrapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { themeBrand, site } = useSiteContext()

  const state = useMemo<AnalyticsState>(() => ({ globalContext: buildPreAuthGlobalContext(themeBrand, site), extras: {} }), [themeBrand, site])

  return <AnalyticsStateContext.Provider value={state}>{children}</AnalyticsStateContext.Provider>
}
